// YouTube IFrame Player API を使った GA4 標準形式の動画再生計測。
//
// 背景: トップページ(urban/local 等)の動画は data-src による遅延ロード＋モーダル再生のため、
// ページ読込時点では iframe に src が無い。GTM 標準の「YouTube動画」トリガーは読込時に
// src を持つ iframe しか監視対象にできないため、後から src を注入するこの構成では計測できない。
// そこで自前で IFrame API を当て、video_start / video_progress / video_complete を push する。

const PROGRESS_THRESHOLDS = [10, 25, 50, 75]; // 進捗計測の閾値(%)。GA4拡張計測/GTM標準と同じセット。

let apiPromise = null;

// IFrame API を一度だけ読み込む。複数箇所から呼ばれても多重ロードしない。
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      if (typeof prev === 'function') { try { prev(); } catch (e) { /* noop */ } }
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(tag, first);
  });
  return apiPromise;
}

function pushDataLayer(player, eventName, extra) {
  const data = (player.getVideoData && player.getVideoData()) || {};
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(Object.assign({
    event: eventName,
    video_provider: 'youtube',
    video_title: data.title || '',
    video_url: (player.getVideoUrl && player.getVideoUrl()) || '',
    video_duration: Math.round((player.getDuration && player.getDuration()) || 0),
    video_current_time: Math.round((player.getCurrentTime && player.getCurrentTime()) || 0),
    visible: true,
  }, extra || {}));
}

function resetState(iframe) {
  iframe._ytState = { started: false, completed: false, progress: {} };
}

function stopPolling(iframe) {
  if (iframe._ytTimer) { clearInterval(iframe._ytTimer); iframe._ytTimer = null; }
}

function startPolling(iframe, player) {
  if (iframe._ytTimer) return;
  iframe._ytTimer = setInterval(() => {
    const dur = player.getDuration();
    if (!dur) return;
    const pct = (player.getCurrentTime() / dur) * 100;
    PROGRESS_THRESHOLDS.forEach((t) => {
      if (pct >= t && !iframe._ytState.progress[t]) {
        iframe._ytState.progress[t] = true;
        pushDataLayer(player, 'video_progress', { video_percent: t });
      }
    });
  }, 1000);
}

function handleStateChange(iframe, player, state) {
  const YT = window.YT;
  if (state === YT.PlayerState.PLAYING) {
    if (!iframe._ytState.started) {
      iframe._ytState.started = true;
      pushDataLayer(player, 'video_start', { video_percent: 0 });
    }
    startPolling(iframe, player);
  } else if (state === YT.PlayerState.PAUSED || state === YT.PlayerState.BUFFERING) {
    stopPolling(iframe);
  } else if (state === YT.PlayerState.ENDED) {
    stopPolling(iframe);
    if (!iframe._ytState.completed) {
      iframe._ytState.completed = true;
      pushDataLayer(player, 'video_complete', { video_percent: 100 });
    }
  }
}

// src を入れ終えた iframe(enablejsapi=1) に計測を有効化する。
// プレーヤーは要素に保持して使い回し、再オープン時は頭出し再生する。
export function attachVideoTracking(iframe) {
  if (!iframe) return;
  resetState(iframe);

  // 生成済みなら作り直さず、頭出しして再生（＝再オープンを新たな再生として計測）
  if (iframe._ytPlayer) {
    try { iframe._ytPlayer.seekTo(0, true); iframe._ytPlayer.playVideo(); } catch (e) { /* noop */ }
    return;
  }

  if (!iframe.id) iframe.id = `yt-${Math.random().toString(36).slice(2)}`;

  loadYouTubeApi().then((YT) => {
    if (iframe._ytPlayer) return; // 非同期中の二重生成ガード
    iframe._ytPlayer = new YT.Player(iframe, {
      events: {
        onReady: () => {
          // autoplay がプレーヤー生成より先に始まっていた場合の開始を取りこぼさない
          const p = iframe._ytPlayer;
          if (p && p.getPlayerState && p.getPlayerState() === YT.PlayerState.PLAYING) {
            handleStateChange(iframe, p, YT.PlayerState.PLAYING);
          }
        },
        onStateChange: (e) => handleStateChange(iframe, iframe._ytPlayer, e.data),
      },
    });
  });
}

// モーダルを閉じた時など。再生を止めるが計測プレーヤーは破棄しない（再利用するため）。
// プレーヤー未生成（API未ロード等）の場合は true を返し、呼び出し側の src クリア等で停止させる。
export function pauseVideoTracking(iframe) {
  if (!iframe) return true;
  stopPolling(iframe);
  if (iframe._ytPlayer && iframe._ytPlayer.pauseVideo) {
    try { iframe._ytPlayer.pauseVideo(); } catch (e) { /* noop */ }
    return false;
  }
  return true;
}
